/**
 * AkiCopywriter — modal that lets users generate AI copy for any part of their site.
 *
 * Uses backend `/api/aki/generate` powered by Claude Sonnet 4.5 via Emergent LLM key.
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <AkiCopywriter open={open} onClose={()=>setOpen(false)} onInsert={(text)=>{...}} kind="hero_headline" brand="Kindred" />
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, X, Loader2, ClipboardCopy, Check, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TONES = [
  'warm and professional',
  'bold and confident',
  'playful and creative',
  'quiet and editorial',
  'friendly and approachable',
  'luxury and refined',
];

export default function AkiCopywriter({ open, onClose, onInsert, defaultKind = 'hero_headline', brand = '', description = '' }) {
  const [kinds, setKinds] = useState([]);
  const [kind, setKind] = useState(defaultKind);
  const [brandInput, setBrandInput] = useState(brand);
  const [descInput, setDescInput] = useState(description);
  const [tone, setTone] = useState(TONES[0]);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/aki/kinds`).then((r) => setKinds(r.data.kinds || [])).catch(() => {});
    setBrandInput(brand);
    setDescInput(description);
    setKind(defaultKind);
    setOptions([]);
  }, [open, brand, description, defaultKind]);

  const generate = async () => {
    if (!brandInput.trim() || !descInput.trim()) {
      toast.error('Please fill in the brand and description');
      return;
    }
    setLoading(true);
    setOptions([]);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/aki/generate`, {
        kind, brand: brandInput, description: descInput, tone,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setOptions(r.data.options || []);
      if ((r.data.options || []).length === 0) {
        toast.error('No suggestions came back — try adjusting the description.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI generation failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, i) => {
    navigator.clipboard.writeText(text);
    setCopied(i);
    setTimeout(() => setCopied(null), 1200);
  };

  const insertOption = (text) => {
    if (onInsert) onInsert(text);
    toast.success('Inserted into your site');
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="aki-modal">
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl border border-white/10 bg-[#171A2C] shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/8 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] flex items-center justify-center shadow-[0_8px_24px_rgba(139,92,246,0.35)]">
              <Sparkles size={18} className="text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#A78BFA]">Aki · AI Copywriter</p>
              <h3 className="text-white font-bold text-lg font-syne">Write with AI</h3>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5" data-testid="aki-close">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Kind picker */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/60">Generate</label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="aki-kinds">
              {kinds.map((k) => (
                <button
                  key={k.id}
                  onClick={() => setKind(k.id)}
                  className={`text-left px-3 py-2 rounded-lg border transition-all ${
                    kind === k.id
                      ? 'border-[#A78BFA] bg-[#A78BFA]/10 shadow-[0_0_0_1px_rgba(167,139,250,0.35)]'
                      : 'border-white/8 bg-white/[0.02] hover:bg-white/[0.04]'
                  }`}
                  data-testid={`aki-kind-${k.id}`}
                >
                  <p className="text-white text-xs font-bold">{k.label}</p>
                  <p className="text-white/50 text-[10px] mt-0.5">{k.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/60">Brand / product name</span>
              <input
                value={brandInput}
                onChange={(e) => setBrandInput(e.target.value)}
                placeholder="e.g. Kindred Studios"
                className="mt-1.5 w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:border-[#A78BFA]/50 outline-none"
                data-testid="aki-brand"
              />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/60">Tone</span>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="mt-1.5 w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#A78BFA]/50 outline-none"
                data-testid="aki-tone"
              >
                {TONES.map((t) => <option key={t} value={t} style={{ background: '#171A2C' }}>{t}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/60">Describe what you do or sell</span>
            <textarea
              rows={3}
              value={descInput}
              onChange={(e) => setDescInput(e.target.value)}
              placeholder="Handcrafted leather goods made in small batches in Portland. We source vegetable-tanned hides..."
              className="mt-1.5 w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:border-[#A78BFA]/50 outline-none resize-none"
              data-testid="aki-description"
            />
          </label>

          <button
            onClick={generate}
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] hover:shadow-[0_12px_30px_rgba(139,92,246,0.45)] text-white text-xs font-bold uppercase tracking-[0.18em] disabled:opacity-50 transition-all"
            data-testid="aki-generate"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
            {loading ? 'Aki is writing…' : 'Generate with AI'}
          </button>

          {/* Options */}
          {options.length > 0 && (
            <div className="space-y-2 pt-2" data-testid="aki-options">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300 flex items-center gap-1.5">
                <Sparkles size={11} /> {options.length} suggestions
              </p>
              {options.map((opt, i) => (
                <div
                  key={i}
                  className="group flex items-start gap-2 p-3 rounded-lg bg-white/[0.03] border border-white/8 hover:border-[#A78BFA]/30 transition-all"
                  data-testid={`aki-option-${i}`}
                >
                  <p className="flex-1 text-sm text-white/90 whitespace-pre-wrap">{opt}</p>
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => copyToClipboard(opt, i)}
                      className="p-1.5 rounded text-white/60 hover:text-white hover:bg-white/5"
                      title="Copy"
                      data-testid={`aki-copy-${i}`}
                    >
                      {copied === i ? <Check size={12} className="text-emerald-400" /> : <ClipboardCopy size={12} />}
                    </button>
                    {onInsert && (
                      <button
                        onClick={() => insertOption(opt)}
                        className="px-2.5 py-1.5 rounded bg-[#A78BFA] text-[#0E0E23] text-[10px] font-bold uppercase tracking-wider hover:bg-white"
                        data-testid={`aki-insert-${i}`}
                      >
                        Use this
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
